import { inject, Injectable } from "@angular/core";
import { map, Observable, of } from "rxjs";
import { CrudService } from "src/app/controls/base/crud-service.interface";
import { SpeedTrial } from "../models/speed-trial.interface";
import { ApiDataService } from "src/app/api/services/api-data.service";
import { GridDataProvider } from "src/app/controls/grid/services/grid-data-provider.interface";
import { ColumnDefinition } from "src/app/controls/grid/models/column-definition.interface";
import { StageCardService } from "../../competition-management/edit-competition-card/edit-stage-card/service/stage-card.service";
import { ResultType } from "../enums/result-type.enum";
import { DynamicSpeedTrial } from "../models/dynamic-speed-trial.interface";
import { RoundResultsTableService } from "../../round-result/services/round-results-table.service";
import { CompetitionResultsTableService } from "../../competition-result/services/competition-results-table.service";

@Injectable()
export class SpeedTrialService implements GridDataProvider, CrudService<SpeedTrial> {

    /** Api data service reference */
    private service = inject(ApiDataService);

    /** Stage service reference */
    private stageService = inject(StageCardService);

    /** Round service reference */
    private roundService = inject(RoundResultsTableService);

    private competitionService = inject(CompetitionResultsTableService);

    /** @inheritdoc */
    public get(id: number): Observable<SpeedTrial> {
        return this.service.get<SpeedTrial>('speed-trial-results/get/' + id);
    }

    /** @inheritdoc */
    public create(entity: SpeedTrial): Observable<number> {
        return this.service.post<number>('speed-trial-results/create', entity);
    }

    /** @inheritdoc */
    public update(entity: SpeedTrial): Observable<boolean> {
        return this.service.put<boolean>('speed-trial-results/update', entity);
    }

    /** @inheritdoc */
    public getTableName(identifier: number, type: ResultType): Observable<string> {
        switch (type) {
            case ResultType.STAGE: return this.stageService.getName(identifier);
            case ResultType.ROUND: return this.roundService.getTableName(identifier);
            case ResultType.COMPETITION: return this.competitionService.getTableName(identifier);
            default: return of();
        }
    }

    /** @inheritdoc */
    public getGridData(identifier: number, type: ResultType): Observable<DynamicSpeedTrial[]> {
        return this.service.post<DynamicSpeedTrial[]>('speed-trial-results/list', { id: identifier, params: ResultType[type] })
            .pipe(
                map((columns: DynamicSpeedTrial[]) => {
                    if (type !== ResultType.STAGE) {
                        columns.map((result) => ({
                            ...result,
                            results: this.mapResults(result),
                          }));
                    }
                    return columns;
                })
            );
    }

    /**
     * Maps the dynamic result columns
     * @param result the obtained result to map
     */
    private mapResults(speedTrial: DynamicSpeedTrial): void {
        for (const result of speedTrial?.results) {
            speedTrial[result.columnId] = result.value;
        }
    }

    /** @inheritdoc */
    public getColumnDefinition(identifier: number, type: ResultType): Observable<ColumnDefinition[]> {
        return this.service.post<ColumnDefinition[]>('speed-trial-results/column-definition', { id: identifier, params: ResultType[type] });
    }
}